import React, { useEffect, useMemo, useState } from "react";
import RecruitmentsTable from "./RecruitmentsTable";
import TableActions from "../molecules/TableActions";
import axios from "axios";
import { API_BASE_URL } from "../../../config/api";

/* ---------- Tiny KPI card ---------- */
const StatCard = ({ label, value, sub }) => (
  <div className="rounded-xl border bg-white shadow-sm p-4">
    <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-gray-900">{value ?? "—"}</div>
    {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
  </div>
);

/* ---------- VERY SIMPLE DONUT (no clicks, distinct colors, clear legend) ---------- */
const SimpleDonut = ({ size = 240, thickness = 30, segments = [], centerTop = "", centerBottom = "" }) => {
  const total = Math.max(1, segments.reduce((a, s) => a + (s.value || 0), 0));
  const cx = size / 2, cy = size / 2;
  const rOuter = size / 2 - 4;
  const rInner = rOuter - thickness;

  let acc = 0;
  const paths = segments.map((s, i) => {
    const ratio = (s.value || 0) / total;
    const start = acc;
    const end = acc + ratio;
    acc = end;

    const a1 = 2 * Math.PI * start - Math.PI / 2;
    const a2 = 2 * Math.PI * end - Math.PI / 2;

    const x1o = cx + rOuter * Math.cos(a1);
    const y1o = cy + rOuter * Math.sin(a1);
    const x2o = cx + rOuter * Math.cos(a2);
    const y2o = cy + rOuter * Math.sin(a2);

    const x1i = cx + rInner * Math.cos(a1);
    const y1i = cy + rInner * Math.sin(a1);
    const x2i = cx + rInner * Math.cos(a2);
    const y2i = cy + rInner * Math.sin(a2);

    const large = end - start > 0.5 ? 1 : 0;

    const d = `
      M ${x1o} ${y1o}
      A ${rOuter} ${rOuter} 0 ${large} 1 ${x2o} ${y2o}
      L ${x2i} ${y2i}
      A ${rInner} ${rInner} 0 ${large} 0 ${x1i} ${y1i}
      Z
    `;

    return <path key={i} d={d} fill={s.color} />;
  });

  return (
    <div className="w-full">
      <div className="flex items-start gap-6">
        <svg width={size} height={size} className="shrink-0">
          <circle cx={cx} cy={cy} r={rOuter} fill="#f7f7f7" />
          {paths}
          <circle cx={cx} cy={cy} r={rInner} fill="white" />
          {centerTop && (
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="18" fontWeight="700" fill="#111827">
              {centerTop}
            </text>
          )}
          {centerBottom && (
            <text x={cx} y={cy + 16} textAnchor="middle" fontSize="11" fill="#6b7280">
              {centerBottom}
            </text>
          )}
        </svg>

        {/* Legend */}
        <ul className="grow space-y-2">
          {segments.map((s, i) => (
            <li key={i} className="flex items-center gap-2 text-sm">
              <span className="inline-block w-3 h-3 rounded" style={{ background: s.color }} />
              <span className="text-gray-800">{s.label}</span>
              <span className="ml-auto text-gray-600">{s.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const TablePages = () => {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const [showStatsModal, setShowStatsModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const timer = setTimeout(() => {
      const url = searchQuery.trim()
        ? `${API_BASE_URL}/api/search?q=${encodeURIComponent(searchQuery)}`
        : `${API_BASE_URL}/api/requests`;

      fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
        .then((res) => res.json())
        .then((data) => setRequests(data.data || []))
        .catch((err) => {
          if (err.name !== "AbortError") console.error("Search error:", err);
        });
    }, 400);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!selectedRequest) return;

    if (!window.confirm(`Are you sure you want to delete request ${selectedRequest.requestId}?`)) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE_URL}/api/requests/${selectedRequest.requestId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests((prev) => prev.filter((r) => r.requestId !== selectedRequest.requestId));
      setSelectedRequest(null);
    } catch (error) {
      console.error("Failed to delete request:", error);
      alert("Failed to delete request. Please try again.");
    }
  };

  const handleEditOpen = () => {
    if (!selectedRequest) return;
    setEditForm({
      positionTitle: selectedRequest.positionTitle || "",
      department: selectedRequest.department || "",
      positionType: selectedRequest.positionType || "",
    });
    setShowEditModal(true);
  };

  const handleEditChange = (field, value) => setEditForm((p) => ({ ...p, [field]: value }));

  const handleEditSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const requestId = selectedRequest?.requestId;
      const res = await axios.put(`${API_BASE_URL}/api/requests/${requestId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests((prev) => prev.map((r) => (r.requestId === requestId ? res.data.data : r)));
      setSelectedRequest(res.data.data);
      setShowEditModal(false);
    } catch (err) {
      console.error("Edit failed:", err);
      alert("Failed to save changes. Check console for details.");
    }
  };

  const handleStatsOpen = () => {
    if (!selectedRequest) {
      alert("Please select a request first.");
      return;
    }
    setShowStatsModal(true);
  };

  /* ---------- Data for the dashboard ---------- */
  const stats = useMemo(() => {
    const seed = Number(String(selectedRequest?.requestId || 7).slice(-2)) || 7;

    const totalApps = 120 + (seed % 40);
    const validApps = Math.round(totalApps * 0.88);
    const duplicateApps = Math.max(2, Math.round(totalApps * 0.04));
    const incomplete = Math.max(0, totalApps - validApps - duplicateApps);

    const daysToDeadline = (seed % 12) + 3;
    const avgPerDay = Math.round(totalApps / 12);
    const firstAppAt = "2025-10-02 09:15";
    const lastAppAt = "2025-10-16 16:42";
    const peakDay = "2025-10-10";
    const first10ElapsedHrs = 26 + (seed % 10);
    const medianSubmitMins = 38 + (seed % 15);
    const started = totalApps + 14;
    const completionRate = Math.round((totalApps / Math.max(1, started)) * 100);

    const approvals = [
      { seq: 1, stage: "HOD", status: "Approved", startedAt: "2025-09-28 10:12", completedAt: "2025-09-28 16:20", days: 0.25, slaDays: 2, breached: false },
      { seq: 2, stage: "HR Ops SM", status: "Approved", startedAt: "2025-09-29 09:00", completedAt: "2025-09-29 17:30", days: 0.35, slaDays: 2, breached: false },
      { seq: 3, stage: "CFO", status: "Approved", startedAt: "2025-09-30 09:10", completedAt: "2025-10-02 11:45", days: 2.11, slaDays: 2, breached: true },
      { seq: 4, stage: "GM HR", status: "Approved", startedAt: "2025-10-02 13:00", completedAt: "2025-10-03 10:05", days: 0.88, slaDays: 2, breached: false },
      { seq: 5, stage: "CEO", status: "Approved", startedAt: "2025-10-03 11:00", completedAt: "2025-10-04 09:30", days: 0.94, slaDays: 2, breached: false },
    ];
    const slaBreaches = approvals.filter((a) => a.breached).length;
    const currentStage = "Advertisement";
    const e2eToAdvertisement = "5.0 days";
    const rejectionsCount = 0;
    const revisionsCount = 1;

    const requesterActivity = { comments: 4, attachments: 3, lastUpdated: "2025-10-04 09:35" };

    const publishing = {
      status: "Published",
      publishedAt: "2025-10-04 12:00",
      deadlineAt: "2025-10-28 23:59",
      autoHideAfterDeadline: true,
      daysRemaining: daysToDeadline,
    };

    const traffic = {
      pageViews: 1480 + seed,
      uniqueVisitors: 930 + (seed % 40),
      topSources: [
        { source: "Direct / Bookmark", count: 540 },
        { source: "LinkedIn", count: 380 },
        { source: "MTN Careers Portal", count: 320 },
        { source: "Referral Links", count: 150 },
        { source: "Other", count: 120 },
      ],
    };

    const sources = {
      Careers: Math.round(totalApps * 0.72),
      Referral: Math.round(totalApps * 0.18),
      "Manual Import": totalApps - Math.round(totalApps * 0.72) - Math.round(totalApps * 0.18),
    };
    const internal = Math.round(totalApps * 0.2);
    const external = totalApps - internal;

    const education = {
      Diploma: 18 + (seed % 6),
      Bachelors: 72 + ((seed + 2) % 9),
      Masters: 35 + ((seed + 3) % 6),
      "Other/Cert": 10 + ((seed + 1) % 4),
    };

    const withCv = totalApps - 1;
    const withCover = Math.round(totalApps * 0.82);
    const withCertificates = Math.round(totalApps * 0.6);

    const pipeline = {
      shortlisting: { status: "In Progress", shortlisted: 38, panelAssigned: true, owner: "HR Team", notes: "Auto-rules + manual review" },
      exam:         { status: "Scheduled",   scheduledOn: "2025-10-20 09:00", invited: 30, venue: "Online (Proctored)" },
      interview:    { status: "Pending",     rounds: 2, panelReady: true, candidates: 18 },
      vetting:      { status: "Pending",     checks: ["References", "Employment", "Criminal"], providerReady: false },
      documentation:{ status: "Pending",     required: ["ID", "Certificates", "Tax Number"], collected: 0 },
    };

    return {
      kpis: {
        totalApps, validApps, duplicateApps, incomplete, daysToDeadline,
        avgPerDay, firstAppAt, lastAppAt, peakDay, first10ElapsedHrs, medianSubmitMins,
        started, completionRate, internal, external,
        currentStage, e2eToAdvertisement, slaBreaches, rejectionsCount, revisionsCount, requesterActivity,
        withCv, withCover, withCertificates
      },
      approvals, publishing, traffic, sources, education, pipeline
    };
  }, [selectedRequest]);

  /* ---------- Pie chart data (simplified) ---------- */
  const processDonut = useMemo(() => ([
    { label: "Approvals",            value: Math.max(1, 5 - stats.kpis.slaBreaches), color: "#2563eb" }, // blue
    { label: "Publishing",           value: Math.max(1, stats.publishing.daysRemaining), color: "#ffcb05" }, // MTN yellow
    { label: "Applications Timeline",value: Math.max(1, stats.kpis.avgPerDay), color: "#10b981" }, // emerald
  ]), [stats]);

  const qualityDonut = useMemo(() => ([
    { label: "Valid",      value: stats.kpis.validApps,     color: "#10b981" }, // green
    { label: "Incomplete", value: stats.kpis.incomplete,    color: "#f97316" }, // orange
    { label: "Duplicates", value: stats.kpis.duplicateApps, color: "#ef4444" }, // red
  ]), [stats]);

  return (
    <div className="w-full h-screen p-2 mb-2 bg-gray-50">
      <h2 className="mb-2 text-2xl font-MTNfont">Request</h2>

      <div className="space-y-2">
        <TableActions
          selectedRequestId={selectedRequest?.requestId}
          onSearch={(value) => setSearchQuery(value)}
          onEdit={handleEditOpen}
          onDelete={handleDelete}
          onStats={handleStatsOpen}
        />

        <RecruitmentsTable
          requests={requests}
          selectedRequest={selectedRequest}
          onSelectRequest={setSelectedRequest}
        />
      </div>

      {/* Edit Modal (unchanged) */}
      {showEditModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-lg p-6 space-y-4 bg-white rounded shadow-lg">
            <h3 className="text-xl font-semibold">Edit Request</h3>

            <label className="block">
              <span className="text-sm">Position Title</span>
              <input
                type="text"
                value={editForm.positionTitle}
                onChange={(e) => handleEditChange("positionTitle", e.target.value)}
                className="w-full p-2 mt-1 border rounded"
              />
            </label>

            <label className="block">
              <span className="text-sm">Department</span>
              <input
                type="text"
                value={editForm.department}
                onChange={(e) => handleEditChange("department", e.target.value)}
                className="w-full p-2 mt-1 border rounded"
              />
            </label>

            <label className="block">
              <span className="text-sm">Position Type</span>
              <input
                type="text"
                value={editForm.positionType}
                onChange={(e) => handleEditChange("positionType", e.target.value)}
                className="w-full p-2 mt-1 border rounded"
              />
            </label>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={handleEditSave} className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && selectedRequest && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/60">
          <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden my-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ backgroundColor: "#ffcb05" }}>
              <div>
                <h3 className="text-lg font-extrabold text-black tracking-tight">Request Dashboard</h3>
                <p className="text-sm text-black/80">
                  {selectedRequest?.requestId} • {selectedRequest?.positionTitle || "—"} • {selectedRequest?.department || "—"}
                </p>
              </div>
              <button className="px-3 py-1.5 rounded-md text-sm font-medium bg-black text-white hover:opacity-90" onClick={() => setShowStatsModal(false)}>
                Close
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6 max-h-[86vh] overflow-y-auto">
              {/* Top KPIs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Applications (Total)" value={stats.kpis.totalApps} />
                <StatCard label="Valid Applications" value={stats.kpis.validApps} sub="Complete & non-duplicate" />
                <StatCard label="Duplicates" value={stats.kpis.duplicateApps} />
                <StatCard label="Days to Deadline" value={stats.kpis.daysToDeadline} />
              </div>

              {/* Two simple donuts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border bg-white p-4">
                  <SimpleDonut
                    size={240}
                    thickness={30}
                    segments={processDonut}
                    centerTop="Overview"
                    centerBottom="Process"
                  />
                  <div className="mt-3 text-sm rounded-xl border bg-white p-3">
                    <div><strong>Current Stage:</strong> {stats.kpis.currentStage}</div>
                    <div><strong>End-to-End to Advertisement:</strong> {stats.kpis.e2eToAdvertisement}</div>
                    <div><strong>SLA Breaches:</strong> {stats.kpis.slaBreaches} • <strong>Revisions:</strong> {stats.kpis.revisionsCount} • <strong>Rejections:</strong> {stats.kpis.rejectionsCount}</div>
                    <div><strong>Visibility:</strong> {stats.publishing.publishedAt} → {stats.publishing.deadlineAt} • <strong>Traffic:</strong> {stats.traffic.pageViews} views / {stats.traffic.uniqueVisitors} unique</div>
                    <div><strong>Apps Timeline:</strong> Avg/Day {stats.kpis.avgPerDay} • First/Last {stats.kpis.firstAppAt} / {stats.kpis.lastAppAt} • Peak {stats.kpis.peakDay}</div>
                  </div>
                </div>

                <div className="rounded-xl border bg-white p-4">
                  <SimpleDonut
                    size={240}
                    thickness={30}
                    segments={qualityDonut}
                    centerTop={`${Math.round((stats.kpis.validApps / Math.max(1, stats.kpis.totalApps)) * 100)}%`}
                    centerBottom="Valid"
                  />
                  <div className="mt-3 text-sm rounded-xl border bg-white p-3">
                    <div><strong>Completion Rate:</strong> {stats.kpis.completionRate}% (Started: {stats.kpis.started})</div>
                    <div><strong>Mix:</strong> Internal {stats.kpis.internal} • External {stats.kpis.external}</div>
                    <div><strong>Education:</strong> {Object.entries(stats.education).map(([k,v]) => `${k} ${v}`).join(" • ")}</div>
                    <div><strong>Documents:</strong> CVs {stats.kpis.withCv} • Cover Letters {stats.kpis.withCover} • Certificates {stats.kpis.withCertificates}</div>
                  </div>
                </div>
              </div>

              {/* Stage Progress */}
              <div className="rounded-xl border bg-white p-4">
                <h4 className="font-semibold text-gray-800 mb-3">Stage Progress</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-gray-500 uppercase">Shortlisting</div>
                    <div className="mt-1 font-semibold">{stats.pipeline.shortlisting.status}</div>
                    <div className="mt-1 text-sm text-gray-700">Shortlisted: {stats.pipeline.shortlisting.shortlisted}</div>
                    <div className="text-xs text-gray-500">Owner: {stats.pipeline.shortlisting.owner}</div>
                    <div className="text-xs text-gray-500">Panel Assigned: {stats.pipeline.shortlisting.panelAssigned ? "Yes" : "No"}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-gray-500 uppercase">Exam</div>
                    <div className="mt-1 font-semibold">{stats.pipeline.exam.status}</div>
                    <div className="mt-1 text-sm text-gray-700">Scheduled: {stats.pipeline.exam.scheduledOn}</div>
                    <div className="text-xs text-gray-500">Invited: {stats.pipeline.exam.invited}</div>
                    <div className="text-xs text-gray-500">Venue: {stats.pipeline.exam.venue}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-gray-500 uppercase">Interview</div>
                    <div className="mt-1 font-semibold">{stats.pipeline.interview.status}</div>
                    <div className="mt-1 text-sm text-gray-700">Rounds: {stats.pipeline.interview.rounds}</div>
                    <div className="text-xs text-gray-500">Panel Ready: {stats.pipeline.interview.panelReady ? "Yes" : "No"}</div>
                    <div className="text-xs text-gray-500">Candidates: {stats.pipeline.interview.candidates}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-gray-500 uppercase">Vetting</div>
                    <div className="mt-1 font-semibold">{stats.pipeline.vetting.status}</div>
                    <div className="mt-1 text-sm text-gray-700">Checks: {stats.pipeline.vetting.checks.join(", ")}</div>
                    <div className="text-xs text-gray-500">Provider Ready: {stats.pipeline.vetting.providerReady ? "Yes" : "No"}</div>
                  </div>
                  <div className="rounded-lg border p-3">
                    <div className="text-xs text-gray-500 uppercase">Documentation</div>
                    <div className="mt-1 font-semibold">{stats.pipeline.documentation.status}</div>
                    <div className="mt-1 text-sm text-gray-700">Required: {stats.pipeline.documentation.required.join(", ")}</div>
                    <div className="text-xs text-gray-500">Collected: {stats.pipeline.documentation.collected}</div>
                  </div>
                </div>
              </div>

              {/* Approvals timeline */}
              <div className="rounded-xl border bg-white p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-800">Approval Path & Time-in-Stage</h4>
                  <span className="text-xs text-gray-500">SLA breaches highlighted</span>
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600">
                        <th className="py-2 pr-3">#</th>
                        <th className="py-2 pr-3">Stage</th>
                        <th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Started</th>
                        <th className="py-2 pr-3">Completed</th>
                        <th className="py-2 pr-3">Days in Stage</th>
                        <th className="py-2 pr-3">SLA (days)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {stats.approvals.map((r) => (
                        <tr key={r.seq} className="hover:bg-gray-50">
                          <td className="py-2 pr-3">{r.seq}</td>
                          <td className="py-2 pr-3">{r.stage}</td>
                          <td className="py-2 pr-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              r.status === "Approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                            }`}>{r.status}</span>
                          </td>
                          <td className="py-2 pr-3">{r.startedAt}</td>
                          <td className="py-2 pr-3">{r.completedAt}</td>
                          <td className="py-2 pr-3">{r.days}</td>
                          <td className="py-2 pr-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              r.breached ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
                            }`}>{r.slaDays}{r.breached ? " • Breached" : ""}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Traffic Top Sources */}
              <div className="rounded-xl border bg-white p-4">
                <h4 className="font-semibold text-gray-800 mb-2">Top Traffic Sources</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600">
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Visits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {stats.traffic.topSources.map((s, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="py-2 pr-3">{s.source}</td>
                        <td className="py-2 pr-3">{s.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer bar */}
            <div className="h-2" style={{ backgroundColor: "#1a1a1a" }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TablePages;
